import { QueryClient } from '@tanstack/react-query';
import { GetServerSideProps } from 'next';
import { NextSeo } from 'next-seo';
import { useEffect } from 'react';
import Error404 from '~/components/Error404';
import Error500 from '~/components/Error500';
import { SharedFeatureFlags } from '~/components/FeatureFlagControls/SharedFeatureFlags';
import { logger } from '~/core/logger/logger';
import { MediaKit, getMKQueryKeyFn } from '~/hooks/api/media-kits/get';
import { useSharedLinkAccessEmail } from '~/hooks/api/useSavedLists/useSharedLinkAccessEmail';
import { processOgImages } from '~/metaDataUtils';
import { MediaKitPreview } from '~/screens/MediaKitPreview';
import { MediaKitFooter } from '~/screens/MediaKitPreview/Footer';
import { MadeWithFOAM } from '~/screens/MediaKitPreview/MadeWithFOAM';
import { MEDIA_KIT_EVENTS } from '~/screens/MediaKitPreview/mediaKitAccessEvents';
import { mediaKitIds } from '~/screens/MediaKitPreview/mkMigrations';
import { MEDIA_KITS_DD_SECTION } from '~/screens/PageBuilder';
import { ShareLinkAccessEventProvider } from '~/screens/SharedSavedList/ShareLinkAccessEventContext';
import { useShareLinkAccessTracking } from '~/screens/SharedSavedList/useShareLinkAccessTracking';
import shareLinkApi from '~/shareLinkApi';
import { mapShareLinkFromApiToVm } from '~/shareLinkApi/shareLink.mappers';

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SHARE_TOKEN_LENGTH = 8;

/**
 * What viewer-activity tracking needs from the share link. Only the id is passed
 * through: Next requires strictly JSON-serialisable props, and the full mapped
 * share link carries optional fields that can be undefined.
 */
type SharedMediaKitLink = { id: string };

interface Props {
  mediaKit: MediaKit | null;
  /** Null on the legacy UUID flow, which has no share link and so cannot be tracked. */
  shareLink: SharedMediaKitLink | null;
  /** Guest key for the shareable feature flags. Null on the legacy UUID flow. */
  shareToken: string | null;
  error: { message: string; code: number } | null;
}

const MediaKitSharedPage = ({
  mediaKit,
  shareLink,
  shareToken,
  error,
}: Props) => {
  // Viewer-activity tracking for the Shareable Activity insight (FOAM-21931),
  // reusing the shared list's plumbing so both surfaces mint one access per
  // viewer per link and send an identical payload shape. The email is read from
  // the cookie every shared surface writes, so a viewer already known from a
  // shared list stays the same viewer here.
  const { accessEmail, accessEmailSource } = useSharedLinkAccessEmail();
  const { track } = useShareLinkAccessTracking({
    link: shareLink ?? undefined,
    openedEventType: MEDIA_KIT_EVENTS.opened,
    accessEmail,
    accessEmailSource,
  });

  useEffect(() => {
    if (!error) return;

    logger
      .withMetadata({
        mediaKitId: mediaKit?.id,
        section: MEDIA_KITS_DD_SECTION,
        error,
      })
      .error('[Shared page] Media Kit loading error: ' + error.code);
  }, [error]);

  if (error?.code === 404) {
    return <Error404 />;
  }

  if (error || !mediaKit) {
    return <Error500 />;
  }

  const { name, thumbnail, id: mediaKitId } = mediaKit;
  const pageUrl = `${process.env.NEXT_PUBLIC_APP_DOMAIN}/shared/media-kits/${mediaKitId}`;

  return (
    <div className="bg-[var(--media-kit-background-primary)]">
      {/* Resolves the feature flags for anonymous viewers. */}
      <SharedFeatureFlags shareToken={shareToken ?? undefined} />
      <div className="tab:p-10">
        <NextSeo
          title={name}
          openGraph={{
            title: name,
            description: name,
            type: 'website',
            images: thumbnail ? processOgImages([thumbnail], 'mediaKit') : [],
            siteName: 'FOAM',
            url: pageUrl,
          }}
          twitter={{
            cardType: 'summary_large_image',
          }}
        />
        <div>
          <ShareLinkAccessEventProvider track={track}>
            <MediaKitPreview data={mediaKit} />
          </ShareLinkAccessEventProvider>
        </div>
        <MadeWithFOAM />
      </div>
      <MediaKitFooter agencyId={mediaKit.agency_id} />
    </div>
  );
};

const errorProps = (code: number, message: string) => ({
  props: {
    isSSR: true,
    mediaKit: null,
    shareLink: null,
    shareToken: null,
    error: { message, code },
  },
});

export const getServerSideProps: GetServerSideProps = async ({ params }) => {
  const queryClient = new QueryClient();
  const segment = params?.id as string;

  // Legacy short id → redirect to the new UUID with 301
  if (mediaKitIds[segment]) {
    return {
      redirect: {
        destination: `/shared/media-kits/${mediaKitIds[segment]}`,
        permanent: true,
      },
    };
  }

  const isUuid = UUID_REGEX.test(segment);
  const isShareToken = segment?.length === SHARE_TOKEN_LENGTH;

  // Neither a UUID nor an 8-char token → 404 without hitting any backend
  if (!isUuid && !isShareToken) {
    return errorProps(404, 'Invalid media kit link');
  }

  try {
    let entityId = segment;
    let shareToken: string | undefined;
    let shareLinkForTracking: SharedMediaKitLink | null = null;

    if (isShareToken) {
      // Step 1: resolve the share link (X-AUTH-TOKEN guest key sent by shareLinkApi)
      const shareLink = await shareLinkApi
        .get(`share-links/${segment}`)
        .then(({ data }) => mapShareLinkFromApiToVm(data));

      if (shareLink.entityType !== 'media_kit') {
        return errorProps(404, 'Share link does not point to a media kit');
      }

      entityId = shareLink.entityId;
      shareToken = segment;
      shareLinkForTracking = { id: shareLink.id };
    }

    // Step 2 (token flow) / legacy: fetch the media kit.
    // The share token is forwarded for access tracing when present.
    const mediaKit = await queryClient.fetchQuery({
      ...getMKQueryKeyFn(entityId, shareToken),
      staleTime: 5 * 60 * 1000,
    });

    return {
      props: {
        isSSR: true,
        mediaKit,
        shareLink: shareLinkForTracking,
        shareToken: shareToken ?? null,
        error: null,
      },
    };
  } catch (err: any) {
    const errorCode = err.response?.status ?? 500;

    return errorProps(errorCode, err.message);
  }
};

export default MediaKitSharedPage;
